import { useState } from 'react';
import { addDays, format } from 'date-fns';
import { getResizedImage } from 'shared-helpers/common';
import { getOpeningHoursStatus } from 'shared-helpers/courses';
import {
  dateFnsLocales,
  getMessageWithPlaceholders,
  localeLanguageCodes
} from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getCustomContentBasedOnLoginStrategy } from 'lib/common';
import { RichTextValue } from 'components/common';
import { Icon } from 'components/common/icons';
import { PageCustomContent, PagePlain } from 'components/page';
import { LoginContainer } from '.';

import type { GetCourseInfoRes } from 'shared-types/responses';

interface Props {
  course: GetCourseInfoRes['course'];
  next?: string | null;
}

const CourseLogin = ({ course, next }: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const [isStrategyLocal, setIsStrategyLocal] = useState(false);

  const handleActiveStrategyChange = (type: string) => {
    if (type === 'local' && !isStrategyLocal) setIsStrategyLocal(true);
    else if (type !== 'local' && isStrategyLocal) setIsStrategyLocal(false);
  };

  const availableAdditionalStrategies =
    course?.userAccess === 'guests'
      ? []
      : serverConfig.authStrategies.filter(
          (strategy) => !strategy.hideFromLogin
        );

  const { customContent, customContentBeside } =
    getCustomContentBasedOnLoginStrategy({
      isStrategyLocal,
      language,
      serverConfig
    });

  const useLocalLogin =
    serverConfig.authEnableLocalLogin &&
    !['guests', 'sso'].includes(course?.userAccess);

  const useGuestLogin =
    course?.userAccess === 'guests' ||
    course?.userAccess === 'registered-and-guests';

  const useCourseBackgroundImage =
    serverConfig.enableCoursePicture &&
    course?.picture &&
    !(
      serverConfig.courseLoginUseLoginBackgroundImage &&
      theme.loginBackgroundImage
    );

  const courseLanguageCode = localeLanguageCodes[language];

  const languageVersion = course?.languageVersions?.[courseLanguageCode]
    ?.isPublished
    ? course.languageVersions[courseLanguageCode]
    : null;

  const courseTitle = languageVersion?.courseTitle || course?.title;

  const courseDescription = languageVersion
    ? languageVersion.courseDescription || ''
    : course?.description;

  const openingHoursStatus = getOpeningHoursStatus({
    openingHours: course?.openingHours,
    timeZone: serverConfig.timeZone
  });

  const getClosedMessage = () => {
    let closedMessage = `${messages.readerErrorClosedDescription} `;

    if (openingHoursStatus?.daysUntilNextOpen === 0) {
      closedMessage += getMessageWithPlaceholders({
        message: messages.courseOpensTodayAlt,
        placeholders: { time: openingHoursStatus?.currentOrNextStartTime }
      });
    } else if (openingHoursStatus?.daysUntilNextOpen === 1) {
      closedMessage += getMessageWithPlaceholders({
        message: messages.courseOpensTomorrowAlt,
        placeholders: { time: openingHoursStatus?.currentOrNextStartTime }
      });
    } else {
      closedMessage += getMessageWithPlaceholders({
        message: messages.courseOpensOnDayAlt,
        placeholders: {
          day: format(
            addDays(new Date(), openingHoursStatus?.daysUntilNextOpen || 0),
            'eeee',
            { locale: dateFnsLocales[language] }
          ),
          time: openingHoursStatus?.currentOrNextStartTime
        }
      });
    }

    closedMessage += '.';

    return closedMessage;
  };

  if (!course) return null;

  return (
    <PagePlain
      backgroundImage={
        useCourseBackgroundImage
          ? getResizedImage({ url: course.picture, size: 1920 })
          : undefined
      }
      backgroundPosition={
        useCourseBackgroundImage ? course.picturePosition : undefined
      }
      padding={0}
      width="wide"
    >
      <div
        className={`content ${
          customContent && customContentBeside
            ? 'content-with-custom-beside'
            : 'content-without-custom-beside'
        }`}
      >
        <div className="course-content">
          <div className="course-pretitle">{messages.authSignInToCourse}</div>

          <div className="course-title">{courseTitle}</div>

          {courseDescription ? (
            <div className="course-description">
              <RichTextValue html={courseDescription} />
            </div>
          ) : null}

          {course.openingHours?.isEnabled ? (
            <div
              className={`opening-hours ${
                openingHoursStatus?.isCurrentlyOpen
                  ? 'opening-hours-open'
                  : 'opening-hours-closed'
              }`}
            >
              <div className="opening-hours-icon">
                <Icon icon={['far', 'clock']} />
              </div>

              {openingHoursStatus?.isCurrentlyOpen ? (
                <div>
                  {`${messages.courseOpenToday} ${openingHoursStatus.currentOrNextStartTime} - ${openingHoursStatus.currentOrNextEndTime}`}
                </div>
              ) : (
                <div>{getClosedMessage()}</div>
              )}
            </div>
          ) : null}
        </div>

        <div className="login-content">
          {customContent ? (
            <div className="custom-content">
              <PageCustomContent customContent={customContent} />
            </div>
          ) : null}

          <div className="login-inner">
            <LoginContainer
              availableAdditionalStrategies={availableAdditionalStrategies}
              handleActiveStrategyChange={handleActiveStrategyChange}
              initialActiveStrategy={
                useLocalLogin &&
                !useGuestLogin &&
                availableAdditionalStrategies.length === 0 &&
                (!['registered', 'registered-and-guests'].includes(
                  course.userAccess
                ) ||
                  !serverConfig.loginPageRegisterLinkAsButton)
                  ? 'local'
                  : null
              }
              localLoginOpenButtonText={
                course.userAccess === 'registered-and-guests' &&
                availableAdditionalStrategies.length === 0
                  ? messages.authSignIn
                  : null
              }
              next={next}
              registrationLinkEnabled={
                useLocalLogin &&
                serverConfig.enableRegistration &&
                ['registered', 'registered-and-guests'].includes(
                  course.userAccess
                )
              }
              useEmbeddedRegister={true}
              useGuestCourseLogin={useGuestLogin}
              useLocalLogin={useLocalLogin}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .content {
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
        }
        .course-content {
          border-bottom: 1px solid ${theme.lightGreyDark};
          flex-grow: 1;
          padding: 20px;
        }
        .course-pretitle {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .course-title {
          color: ${theme.textColor};
          font-size: 2.2rem;
          font-weight: 700;
          margin-top: 10px;
        }
        .course-description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin: 15px 0 5px;
        }
        .opening-hours {
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;;
          gap: 7px;
          margin-top: 25px;
        }
        .opening-hours-open {
          color: ${theme.textColorLight};
        }
        .opening-hours-closed {
          color: ${theme.dangerColor};
        }
        .custom-content {
          padding: 20px 20px 5px;
        }
        .login-content {
          flex-shrink: 0;
          max-width: 100%;
          width: 100%;
        }
        .login-inner {
          padding: 20px;
        }
        .login-title {
          color: ${theme.textColor};
          font-size: 1.8rem;
          font-weight: 700;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .content {
            flex-direction: row;
          }
          .course-content {
            padding: 25px;
          }
          .custom-content {
            padding: 25px 25px 0;
          }
          .login-inner {
            padding: 25px;
          }
          .content-with-custom-beside .course-content {
            width: 100%;
          }
          .content-without-custom-beside .course-content {
            border-bottom: none;
            border-right: 1px solid ${theme.lightGreyDark};
            width: calc(100% - 320px);
          }
          .content-with-custom-beside .login-content {
            display: flex;
          }
          .content-without-custom-beside .login-content {
            width: 320px;
          }
          .content-with-custom-beside .login-inner {
            width: 320px;
          }
          .content-with-custom-beside .custom-content {
            border-right: 1px solid ${theme.lightGreyDark};
            width: calc(100% - 320px);
            padding: 25px;
          }
          .content-without-custom-beside .custom-content {
            width: 320px;
        }
      `}</style>
    </PagePlain>
  );
};

export default CourseLogin;
